import React, { useState } from 'react';
import { Auth, API } from "aws-amplify";
import { useSnackbar } from 'notistack';

import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Grid from "@material-ui/core/Grid";

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import ModalConfirm from '../components/ModalConfirm';


import { buildConfirmResendInfo } from '../libs/buildConfirmText';

const useStyles = makeStyles(theme => ({
    button: {
        margin: '15px',
        width: '150px',
        height: '25px'
    },
    actionButton: {
        margin: '8px',
        minWidth: '140px',
        width: '100%',
        textTransform: 'none',
        justifyContent: 'flex-start',
        paddingLeft: '36px',
    },
    actionButtonSelected: {
        margin: '8px',
        minWidth: '140px',
        width: '100%',
        textTransform: 'none',
        justifyContent: 'flex-start',
        paddingLeft: '8px',
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        }
    },
    helperText: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        padding: '8px 16px',
        minHeight: '60px',
        display: 'flex',
        alignItems: 'center',
    },
    actionContainer: {
        marginBottom: '20px',
    },
    buttonColumn: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '300px',
    },
    narrowbutton: {
        width: '150px',
        margin: '15px',
        height: '30px',
        paddingTop: '5.5px !important',
        paddingBottom: '5.5px !important',
        textAlign: 'center',
    },
    setInLine: {
        display: "inline"
    },

}));

export default function RestartAnmodning(props) {
    const classes = useStyles();
    const [modelOpen, setModalOpen] = useState(false);
    const [modalConfirmed, setModalConfirmed] = useState(false);
    const [resendChecked, setResendChecked] = useState(false);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [action, setAction] = useState('restart');
    
    // Handle both single anmodning and list of IDs
    const isBulkMode = Boolean(props.anmodningRowIds && props.anmodningRowIds.length > 0);
    const hasErrors = props.anmodning?.vendor_data_geojson?.features?.some(f => f.properties?.error_msg) || false;
    const isPaavisning = props.anmodning?.anmod_status === 'Udtaget til paavisning';
    const isFinalStatus = props.status === 'Gennemført' || props.status === 'Håndteret Manuelt';

    const handleCheckChange = (e) => {
        setResendChecked(e.target.checked);
    };

    function handleModalConfirmClosed(newValue) {
        setModalOpen(false);
        setModalConfirmed(newValue);
        if (!newValue)
            return;

        if (isBulkMode) {
            // Handle multiple anmodning IDs
            const anmodIds = props.anmodningRowIds;
            if (!anmodIds || anmodIds.length === 0) {
                enqueueSnackbar('Ingen anmodning IDer fundet', { variant: 'error' });
                return;
            }
            
            // Process each ID sequentially
            let successCount = 0;
            let errorCount = 0;
            
            const processIds = async () => {
                for (const anmodId of anmodIds) {
                    try {
                        await restartAnmodning(anmodId, action, resendChecked);
                        successCount++;
                    } catch (error) {
                        errorCount++;
                        enqueueSnackbar(`Fejl ved genstart af anmodning ${anmodId}: ${error.message}`, { variant: 'error' });
                    }
                }
                
                if (successCount > 0) {
                    enqueueSnackbar(`${successCount} anmodninger behandlet succesfuldt`, { variant: 'success' });
                }
                if (errorCount > 0) {
                    enqueueSnackbar(`${errorCount} anmodninger fejlede`, { variant: 'warning' });
                }
                
                props.onAccecpt();
            };
            
            processIds();
        } else {
            // Handle single anmodning
            const anmodId = props.anmodning?.anmod_id;
            if (!anmodId) {
                enqueueSnackbar('Ingen anmodning ID fundet', { variant: 'error' });
                return;
            }
            restartAnmodning(anmodId, action, resendChecked)
                .then(() => {
                    props.onAccecpt();
                })
                .catch((error) => {
                    enqueueSnackbar('Fejl ved genstart: ' + error.message, { variant: 'error' });
                });
        }
    }

    function restartAnmodning(id, action, sendpakke) {
        enqueueSnackbar(`Sender anmodning id:${id}`, { variant: 'info' });
        return postAnmodningAction(id, action, sendpakke);
    }

    async function postAnmodningAction(anmod_id, action, sendpakke) {
        if (!sendpakke)
            sendpakke = false;
        let init = {
            headers: { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` }
        }
        return await API.post("API", "/Anmodning/restart/" + anmod_id + "/" + action + "/" + sendpakke, init);
    }

    function sendRestart(e) {
        setModalOpen(true);
    }

    const handleActionChange = (newAction) => {
        setAction(newAction);
        setResendChecked(false);
    };

    const allActionOptions = [
        { 
            value: 'restart', 
            label: 'Genstart', 
            helperText: 'Genstarter hele processen fra begyndelsen.\n\n Denne funktion genstarter processen med at hente data fra de opsatte kilder.\n\n Hvis nogle af kilderne mangler at blive hentet pga netværksfejl el. lign kan denne bruges til at hente de resterende.\n\nVær opmærksom på at dette samtidig fjerner de editeringer og en evt. tilbagedatering. ',
            disabled: isPaavisning
        },
        { 
            value: 'transform', 
            label: 'Oversæt', 
            helperText: 'Oversætter data til LER formatet.\n\nBenyt denne hvis du har ændret i det hentede data. Hvis der stadig er fejl i dataene, skal disse rettes manuelt før du manuelt sender svaret til LER.',
            disabled: isPaavisning
        },
        { 
            value: 'quickfix', 
            label: 'Tilbagedater data', 
            helperText: 'For at komme hurtigt videre kan du forsøge at rette nogle af de mest almindelige fejl i dataene automatisk.\n\n\Denne funktion vil fjerne "etableringsdato"-feltet fra data, og undtager dem derfor fra den hårde validering. Hvis der er andre fejl i dataene, skal disse rettes manuelt før du kan sende svar til LER.\n\nDenne funktion oversætter automatisk data lige efter.',
            disabled: isPaavisning
        },
        { 
            value: 'sendmanuelmedoplysninger', 
            label: 'Send manuelt ledningsoplysninger', 
            helperText: 'Sender svar til LER. \n\nBruges typisk hvis du har rettet fejl i dataene, og er klar til at sende svar til LER - eller hvis du ønsker at sende ledningsoplysninger i et område som er markeret til manuel udtagelse.',
            disabled: hasErrors || isPaavisning
        },
        {
            value: 'markasmanuelhaandt',
            label: 'Afslut som \'Håndteret manuelt\'',
            helperText: 'Markér anmodningen som håndteret uden at genstarte nogen processer.\n\nDenne benyttes til at accepteret det svar som allerede er sendt til LER, og for at bekræfte at anmodningen er håndteret af en administrator.\n\nBruges typisk for at markere at påvisning er udført, Eller at anmodningen er håndteret manuelt - udenom LER. \n\n Denne handling sender altså IKKE nogen ledningsoplysninger til graveaktøren! Der vil heller ikke blive genereret en kvitteringsmail.'
        }
    ];

    // If status is final, only allow restart action
    const actionOptions = isFinalStatus 
        ? allActionOptions.filter(option => option.value === 'restart')
        : allActionOptions;

    return (
        <>
            <ModalConfirm
                id="confirm-action"
                keepMounted
                open={modelOpen}
                onClose={handleModalConfirmClosed}
                question={buildConfirmResendInfo(isBulkMode ? props.anmodningRowIds.length : 1, action)}
                value={modalConfirmed} />
            <Typography>{props.infoTxt} </Typography>
            
            <Box className={classes.actionContainer}>
                <Typography variant="subtitle2" style={{ marginBottom: '8px' }}>
                    Vælg handling:
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Box className={classes.buttonColumn}>
                            {actionOptions.map((option) => (
                                <Button
                                    key={option.value}
                                    variant={action === option.value ? "contained" : "outlined"}
                                    color={action === option.value ? "primary" : "default"}
                                    className={action === option.value ? classes.actionButtonSelected : classes.actionButton}
                                    onClick={() => handleActionChange(option.value)}
                                    disabled={option.disabled}
                                    startIcon={action === option.value ? <ChevronRightIcon /> : null}
                                >
                                    {option.label}
                                </Button>
                            ))}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        {action && (
                            <Box className={classes.helperText}>
                                <Typography style={{ whiteSpace: 'pre-line' }}>
                                    {actionOptions.find(opt => opt.value === action)?.helperText}
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Box>

            <div className={classes.setInLine} >
                <Button
                    color="primary"
                    disabled={action.length === 0}
                    variant="contained"
                    className={classes.button}
                    type="button"
                    onClick={sendRestart}
                >Udfør
                </Button>
                {action === 'restart' && (
                    <FormControlLabel style={{ marginLeft: '15px' }}
                        control={<Checkbox
                            checked={resendChecked}
                            onChange={handleCheckChange}
                        />}
                        label="Send ny LER pakke!"
                    />
                )}
            </div>
        </>
    );
}